import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import api, { solicitudesAPI, supervisoresAPI, uploadAPI, npsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCatalogos } from '../context/CatalogosContext';
import { CheckCircle, XCircle, HelpCircle, Image, ZoomIn, Edit, Save, X, ExternalLink, Loader2, MessageCircle, Send, Zap, Triangle, Bell } from 'lucide-react';
import PhotoUpload, { getFileType, FileThumbnail, PreviewContent } from './PhotoUpload';
import VoiceInput from './VoiceInput';
import InspeccionDialog from './InspeccionDialog';
import AtencionDialog from './AtencionDialog';

const SolicitudDetailDialog = ({ solicitud, open, onClose, onUpdate }) => {
  const [supervisores, setSupervisores] = useState([]);
  const [estado, setEstado] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [notas, setNotas] = useState('');
  const [idExterno, setIdExterno] = useState('');
  const [fechaInspeccionProgramada, setFechaInspeccionProgramada] = useState('');
  const [horaInspeccionProgramada, setHoraInspeccionProgramada] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showInspeccionReadOnly, setShowInspeccionReadOnly] = useState(false);
  const [showAtencionReadOnly, setShowAtencionReadOnly] = useState(false);
  const [sendingNps, setSendingNps] = useState(false);
  const [sendingRecordatorio, setSendingRecordatorio] = useState(false);
  const [recordatorioActivo, setRecordatorioActivo] = useState(false);
  const { user } = useAuth();
  
  // Usar catálogos cacheados del context
  const { catalogos, loadCatalogos } = useCatalogos();

  // Actualizar estados cuando cambia solicitud
  useEffect(() => {
    if (solicitud && !solicitud.loading) {
      setEstado(solicitud.estado || '');
      setSupervisorId(solicitud.supervisor_id || '');
      setNotas(solicitud.notas || '');
      setIdExterno(solicitud.id_externo || '');
      setFechaInspeccionProgramada(solicitud.fecha_inspeccion_programada || '');
      setHoraInspeccionProgramada(solicitud.hora_inspeccion_programada || '');
    }
  }, [solicitud]);

  // Contador de fisuras
  const [fisurasCount, setFisurasCount] = useState(null);
  useEffect(() => {
    if (solicitud?.proyecto_id) {
      const params = new URLSearchParams({ proyecto_id: solicitud.proyecto_id });
      if (solicitud.etapa) params.append('etapa', solicitud.etapa);
      if (solicitud.departamento) params.append('departamento', solicitud.departamento);
      api.get(`/solicitudes/fisuras-count?${params}`)
        .then(res => setFisurasCount(res.data))
        .catch(() => setFisurasCount(null));
    }
  }, [solicitud?.proyecto_id, solicitud?.etapa, solicitud?.departamento]);

  // Helper function to parse foto_url (can be JSON string or single URL)
  const parseFotos = (fotoUrl) => {
    if (!fotoUrl) return [];
    try {
      const parsed = JSON.parse(fotoUrl);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [fotoUrl];
    }
  };

  // Convert foto URLs to PhotoUpload format
  const fotosToUploadFormat = (fotoUrl) => {
    const urls = parseFotos(fotoUrl);
    return urls.map(url => ({ url, filePath: null }));
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      try {
        // Si los datos vienen pre-cargados del endpoint optimizado, usarlos
        if (solicitud?._dialogData) {
          setSupervisores(solicitud._dialogData.supervisores || []);
          setRecordatorioActivo(solicitud._dialogData.recordatorio_autoagendamiento_activo ?? false);
          // Los catálogos del context se actualizan si no están cacheados
          if (!catalogos.categorias?.length) {
            await loadCatalogos();
          }
        } else {
          // Fallback: cargar en paralelo si no hay datos pre-cargados
          await Promise.all([
            loadSupervisores(),
            loadCatalogos()
          ]);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    
    // Cargar datos cuando el diálogo se abre Y los datos del requerimiento están listos
    if (open && solicitud && !solicitud.loading) {
      loadInitialData();
    }
  }, [open, solicitud, loadCatalogos, catalogos.categorias?.length]);

  const loadSupervisores = async () => {
    try {
      const response = await supervisoresAPI.getAll();
      setSupervisores(response.data || []);
    } catch (error) {
      console.error('Error loading supervisores:', error);
      toast.error('Error al cargar supervisores');
      setSupervisores([]);
    }
  };

  // Filtered items and fallas based on selected categoria in editingItem
  const filteredItems = useMemo(() => {
    if (!editingItem?.categoria) return [];
    return (catalogos.items || []).filter(item => 
      item.activo !== false && 
      (item.categoria_nombre === editingItem.categoria || !item.categoria_nombre)
    );
  }, [editingItem?.categoria, catalogos.items]);

  const filteredFallas = useMemo(() => {
    if (!editingItem?.categoria) return [];
    return (catalogos.fallas || []).filter(falla => 
      falla.activo !== false && 
      (falla.categoria_nombre === editingItem.categoria || !falla.categoria_nombre)
    );
  }, [editingItem?.categoria, catalogos.fallas]);

  const handleUpdate = async () => {
    // Validar que si uno de los campos de inspección está lleno, el otro también debe estarlo
    const hasFecha = fechaInspeccionProgramada && fechaInspeccionProgramada.trim() !== '';
    const hasHora = horaInspeccionProgramada && horaInspeccionProgramada.trim() !== '';
    
    if ((hasFecha && !hasHora) || (!hasFecha && hasHora)) {
      toast.error('Debe ingresar tanto la fecha como la hora de inspección programada');
      return;
    }
    
    // Validación Stand By Empresa (solo para Fisuras)
    if (estado === 'stand_by_empresa' && solicitud.estado !== 'stand_by_empresa') {
      if (!hasFecha) {
        toast.error('Debe programar una fecha de inspección antes de establecer Stand By Empresa');
        return;
      }
    }
    
    setLoading(true);
    try {
      const updateData = { notas, estado, id_externo: idExterno };
      
      // Solo cambiar automáticamente a 'inspeccion_programada' si se están agregando NUEVOS valores
      const teniaFechaAntes = solicitud.fecha_inspeccion_programada && solicitud.fecha_inspeccion_programada.trim() !== '';
      const teniaHoraAntes = solicitud.hora_inspeccion_programada && solicitud.hora_inspeccion_programada.trim() !== '';
      const esNuevaProgramacion = hasFecha && hasHora && (!teniaFechaAntes || !teniaHoraAntes);
      
      if (esNuevaProgramacion) {
        updateData.estado = 'inspeccion_programada';
      }
      
      // Siempre enviar los campos de fecha/hora si están presentes
      if (hasFecha && hasHora) {
        updateData.fecha_inspeccion_programada = fechaInspeccionProgramada;
        updateData.hora_inspeccion_programada = horaInspeccionProgramada;
      }
      
      if (supervisorId) {
        updateData.supervisor_id = supervisorId;
      }
      await solicitudesAPI.update(solicitud.id, updateData);
      toast.success('Requerimiento actualizado');
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    
    const findCanonical = (list, value) => {
      if (!value) return value;
      const found = list.find(c => c.nombre?.toLowerCase() === value.toLowerCase());
      return found ? found.nombre : value;
    };
    
    setEditingItem({
      ...item,
      categoria: findCanonical(catalogos.categorias, item.categoria),
      lugar: findCanonical(catalogos.lugares, item.lugar),
      item: findCanonical(catalogos.items, item.item),
      falla: findCanonical(catalogos.fallas, item.falla),
      fotos: fotosToUploadFormat(item.foto_url)
    });
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditingItem(null);
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;
    
    setLoading(true);
    try {
      // Convert fotos to JSON string for storage
      const fotoUrls = editingItem.fotos.map(f => f.url || f);
      const fotoUrlJson = fotoUrls.length > 0 ? JSON.stringify(fotoUrls) : null;
      
      // Update the solicitud item via API with all fields
      await uploadAPI.updateSolicitudItem(solicitud.id, editingItem.id, {
        categoria: editingItem.categoria,
        lugar: editingItem.lugar,
        item: editingItem.item,
        falla: editingItem.falla,
        descripcion: editingItem.descripcion,
        foto_url: fotoUrlJson
      });
      
      toast.success('Solicitud actualizada');
      setEditingItemId(null);
      setEditingItem(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Error al actualizar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleFotosChange = (fotos) => {
    setEditingItem(prev => ({ ...prev, fotos }));
  };

  const getEvaluacionBadge = (evaluacion) => {
    switch (evaluacion) {
      case 'procede':
        return { icon: CheckCircle, color: 'bg-green-100 text-green-700', text: 'Procede' };
      case 'no_procede':
        return { icon: XCircle, color: 'bg-red-100 text-red-700', text: 'No Procede' };
      default:
        return { icon: HelpCircle, color: 'bg-gray-100 text-gray-600', text: 'Sin evaluación' };
    }
  };

  const etapas = [
    { key: 'contacto', label: 'Contacto', estados: ['solicitado'] },
    { key: 'inspeccion', label: 'Inspección', estados: ['inspeccion_pendiente', 'inspeccion_programada'] },
    { key: 'atencion', label: 'Atención', estados: ['atencion_pendiente', 'atencion_proceso'] },
    { key: 'cierre', label: 'Cierre', estados: ['terminado', 'cerrado'] },
  ];

  const currentEtapaIndex = solicitud.estado === 'stand_by_empresa'
    ? 1
    : etapas.findIndex(e => e.estados.includes(solicitud.estado));
  
  // Check if inspection has been completed (estado is past inspection phase)
  const hasCompletedInspeccion = ['atencion_pendiente', 'atencion_proceso', 'terminado', 'cerrado', 'stand_by_empresa'].includes(solicitud.estado);
  
  // Check if atencion has been completed (estado is past atencion phase)
  const hasCompletedAtencion = ['terminado', 'cerrado'].includes(solicitud.estado);
  
  // Check if terminated directly from inspection (all solicitudes "no procede")
  const todasNoProcede = useMemo(() => {
    const sols = solicitud?.solicitudes || [];
    if (sols.length === 0) return solicitud?.todas_no_procede || false;
    return sols.every(s => s.evaluacion === 'no_procede');
  }, [solicitud]);
  
  const terminadoDesdeInspeccion = solicitud?.estado === 'terminado' && todasNoProcede;
  const isTerminado = solicitud?.estado === 'terminado' || solicitud?.estado === 'cerrado';
  const isInspeccionCompletada = solicitud?.inspeccion_estado === 'completada';
  
  const handleInspeccionClick = () => {
    setShowInspeccionReadOnly(true);
  };
  
  const handleAtencionClick = () => {
    setShowAtencionReadOnly(true);
  };

  // Handle dialog close - prevent closing if secondary dialogs are open
  const handleDialogOpenChange = (isOpen) => {
    // Only close if no secondary dialogs are open
    if (!isOpen && !showInspeccionReadOnly && !showAtencionReadOnly) {
      onClose();
    } else if (isOpen) {
      // Allow opening
    }
  };

  // Enviar NPS por WhatsApp
  const handleEnviarNps = async () => {
    if (!solicitud?.id) return;
    
    setSendingNps(true);
    try {
      const response = await npsAPI.enviar(solicitud.id);
      toast.success(response.data?.message || 'NPS enviado correctamente');
      // Actualizar el estado local si es necesario
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al enviar NPS';
      toast.error(errorMsg);
    } finally {
      setSendingNps(false);
    }
  };

  // Verificar si se puede enviar NPS
  const canSendNps = solicitud?.estado === 'terminado' && 
    (solicitud?.cliente_telefono || solicitud?.testigo_telefono);

  // Verificar si se puede enviar recordatorio de autoagendamiento
  const canSendRecordatorio = recordatorioActivo &&
    solicitud?.estado === 'solicitado' && 
    !solicitud?.fecha_inspeccion_programada && 
    !solicitud?.hora_inspeccion_programada;

  const handleRecordarAutoagendamiento = async () => {
    if (!solicitud?.id) return;
    
    setSendingRecordatorio(true);
    try {
      const response = await solicitudesAPI.recordarAutoagendamiento(solicitud.id);
      toast.success(response.data?.message || 'Recordatorio enviado correctamente');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al enviar recordatorio';
      toast.error(errorMsg);
    } finally {
      setSendingRecordatorio(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent 
        className="max-w-4xl w-[95vw] sm:w-auto max-h-[90vh] flex flex-col gap-0 p-0"
        preventMobileClose
      >
        <DialogHeader className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-[#E8E8E7] bg-white">
          <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            <div className="flex items-center gap-3 flex-wrap">
              <span>Requerimiento {solicitud?.codigo ? `#${solicitud.codigo}` : ''}</span>
              {solicitud?.primera_respuesta_autoagendamiento_enviada && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" data-testid="primera-respuesta-badge">
                  1ra respuesta enviada
                </span>
              )}
              {fisurasCount && fisurasCount.solicitadas > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200" title="Fisuras: atendidas / solicitadas">
                  <Triangle size={11} />
                  {fisurasCount.atendidas} aten / {fisurasCount.solicitadas} solic
                </span>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Spinner de carga inicial - mostrar si solicitud.loading o initialLoading */}
        {(solicitud?.loading || initialLoading) ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 sm:px-6">
            <Loader2 size={48} className="animate-spin text-[#9d8272] mb-4" />
            <p className="text-sm text-[#5A5A5A]">Cargando formulario...</p>
          </div>
        ) : (
          <>
          <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
          {/* Timeline */}
          <div className="flex items-center justify-between px-2 sm:px-4 overflow-x-auto">
            {etapas.map((etapa, index) => (
              <React.Fragment key={etapa.key}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                      terminadoDesdeInspeccion && etapa.key === 'atencion'
                        ? 'bg-gray-200 text-gray-400'
                        : index <= currentEtapaIndex
                          ? 'bg-[#9d8272] text-white'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {terminadoDesdeInspeccion && etapa.key === 'atencion' ? '—' : index + 1}
                  </div>
                  {/* Make "Inspección" clickable when inspection is completed */}
                  {etapa.key === 'inspeccion' && hasCompletedInspeccion ? (
                    <button
                      onClick={handleInspeccionClick}
                      className={`text-[10px] sm:text-xs mt-1 flex items-center gap-0.5 hover:underline cursor-pointer ${
                        index <= currentEtapaIndex ? 'text-[#9d8272] font-medium' : 'text-gray-400'
                      }`}
                      title="Ver detalles de la inspección"
                    >
                      {etapa.label}
                      <ExternalLink size={10} className="ml-0.5" />
                    </button>
                  ) : etapa.key === 'atencion' && terminadoDesdeInspeccion ? (
                    <span 
                      className="text-[10px] sm:text-xs mt-1 text-gray-400 line-through cursor-not-allowed"
                      title="Este requerimiento fue terminado en la fase de inspección"
                    >
                      {etapa.label}
                    </span>
                  ) : etapa.key === 'atencion' && hasCompletedAtencion ? (
                    <button
                      onClick={handleAtencionClick}
                      className={`text-[10px] sm:text-xs mt-1 flex items-center gap-0.5 hover:underline cursor-pointer ${
                        index <= currentEtapaIndex ? 'text-[#9d8272] font-medium' : 'text-gray-400'
                      }`}
                      title="Ver detalles de la atención"
                    >
                      {etapa.label}
                      <ExternalLink size={10} className="ml-0.5" />
                    </button>
                  ) : etapa.key === 'cierre' && terminadoDesdeInspeccion ? (
                    <span className="text-[10px] sm:text-xs mt-1 text-[#9d8272] font-medium flex items-center gap-0.5">
                      {etapa.label}
                      <Zap size={10} className="text-blue-500" title="Terminado desde inspección" />
                    </span>
                  ) : (
                    <span className={`text-[10px] sm:text-xs mt-1 ${index <= currentEtapaIndex ? 'text-[#9d8272] font-medium' : 'text-gray-400'}`}>
                      {etapa.label}
                    </span>
                  )}
                </div>
                {index < etapas.length - 1 && (
                  <div className={`flex-1 h-0.5 sm:h-1 mx-1 sm:mx-2 min-w-[20px] ${
                    terminadoDesdeInspeccion && etapa.key === 'atencion'
                      ? 'bg-gray-200'
                      : index < currentEtapaIndex ? 'bg-[#9d8272]' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Información del Inmueble */}
          <div className="bg-[#F5F5F5] p-3 sm:p-4 rounded-lg">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3">
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Proyecto</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base truncate">{solicitud.proyecto_nombre}</p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Etapa</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">{solicitud.etapa}</p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Departamento</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">{solicitud.departamento}</p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Fecha Solicitud</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">
                  {new Date(solicitud.fecha_solicitud).toLocaleDateString('es-PE')}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 pt-3 border-t border-[#E8E8E7]">
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Origen</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">{solicitud.origen || 'Portal de Negocios'}</p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Fecha Entrega</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">{solicitud.fecha_entrega || '-'}</p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Garantía Acabados</p>
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                  solicitud.estado_garantia_acabados === 'Vigente' ? 'bg-green-100 text-green-800' :
                  solicitud.estado_garantia_acabados === 'Vencido' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {solicitud.estado_garantia_acabados || 'No Identificado'}
                </span>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Garantía Estructural</p>
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                  solicitud.estado_garantia_estructural === 'Vigente' ? 'bg-green-100 text-green-800' :
                  solicitud.estado_garantia_estructural === 'Vencido' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {solicitud.estado_garantia_estructural || 'No Identificado'}
                </span>
              </div>
            </div>
          </div>

          {/* Información del Titular */}
          <div className="bg-[#F5F5F5] p-3 sm:p-4 rounded-lg">
            <h4 className="font-semibold text-[#2D2D2D] mb-2 sm:mb-3 text-sm sm:text-base" style={{ fontFamily: 'Playfair Display, serif' }}>Datos del Titular</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Nombre</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">{solicitud.cliente_nombre}</p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Teléfono</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">{solicitud.cliente_telefono}</p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Correo</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base truncate">{solicitud.cliente_email}</p>
              </div>
            </div>
          </div>

          {/* Información del Solicitante */}
          <div className={`p-3 sm:p-4 rounded-lg ${
            solicitud.solicitante_nombre && solicitud.solicitante_nombre !== solicitud.cliente_nombre 
              ? 'bg-[#E8F4E8]' 
              : 'bg-[#F0F4FF]'
          }`}>
            <h4 className="font-semibold text-[#2D2D2D] mb-2 sm:mb-3 text-sm sm:text-base" style={{ fontFamily: 'Playfair Display, serif' }}>
              Datos del Solicitante
              {(!solicitud.solicitante_nombre || solicitud.solicitante_nombre === solicitud.cliente_nombre) && (
                <span className="text-[10px] sm:text-sm font-normal text-[#888888] ml-2">(mismo que el Titular)</span>
              )}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Nombre</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">
                  {solicitud.solicitante_nombre || solicitud.cliente_nombre}
                </p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Teléfono</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base">
                  {solicitud.solicitante_telefono || solicitud.cliente_telefono}
                </p>
              </div>
              <div>
                <p className="text-[10px] sm:text-sm text-[#888888] mb-0.5 sm:mb-1">Correo</p>
                <p className="font-semibold text-[#2D2D2D] text-xs sm:text-base truncate">
                  {solicitud.solicitante_email || solicitud.cliente_email}
                </p>
              </div>
            </div>
          </div>

          {/* Solicitudes */}
          <div className="border border-[#E8E8E7] rounded-lg p-3 sm:p-4">
            <h4 className="font-semibold text-[#2D2D2D] mb-2 sm:mb-3 text-sm sm:text-base" style={{ fontFamily: 'Playfair Display, serif' }}>
              Solicitudes ({solicitud.solicitudes?.length || 0})
            </h4>
            
            {solicitud.solicitudes && solicitud.solicitudes.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {solicitud.solicitudes.map((sol, index) => {
                  const evalBadge = getEvaluacionBadge(sol.evaluacion);
                  const EvalIcon = evalBadge.icon;
                  const fotos = parseFotos(sol.foto_url);
                  const isEditing = editingItemId === sol.id;
                  
                  return (
                    <div key={sol.id || index} className={`border rounded-lg p-2 sm:p-3 ${isEditing ? 'border-[#9d8272] bg-white' : 'border-[#E8E8E7] bg-[#FAFAFA]'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs sm:text-sm font-semibold text-[#9d8272]">#{index + 1}</span>
                          <Badge variant="outline" className={`${evalBadge.color} text-[10px] sm:text-xs`}>
                            <EvalIcon size={10} className="mr-0.5 sm:mr-1" />
                            {evalBadge.text}
                          </Badge>
                        </div>
                        
                        {/* Edit/Save buttons */}
                        {user?.rol !== 'cliente' && !isTerminado && (
                          <div className="flex items-center gap-1">
                            {isEditing ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleSaveItem}
                                  disabled={loading}
                                  className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                >
                                  <Save size={14} className="mr-1" />
                                  <span className="text-xs">Guardar</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleCancelEdit}
                                  className="h-7 px-2 text-gray-500 hover:text-gray-700"
                                >
                                  <X size={14} />
                                </Button>
                              </>
                            ) : (
                              <>
                                {fotos.length > 0 && (
                                  <span className="text-[10px] sm:text-xs text-[#9d8272] flex items-center mr-2">
                                    <Image size={12} className="mr-1" />
                                    {fotos.length}
                                  </span>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditItem(sol)}
                                  className="h-7 px-2 text-[#9d8272] hover:text-[#8a7162] hover:bg-[#9d8272]/10"
                                >
                                  <Edit size={14} className="mr-1" />
                                  <span className="text-xs hidden sm:inline">Editar</span>
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* View mode */}
                      {!isEditing && (
                        <>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
                            <div>
                              <p className="text-[#888888] text-[10px] sm:text-xs">Categoría</p>
                              <p className="font-medium">{sol.categoria}</p>
                            </div>
                            <div>
                              <p className="text-[#888888] text-[10px] sm:text-xs">Lugar</p>
                              <p className="font-medium">{sol.lugar}</p>
                            </div>
                            <div>
                              <p className="text-[#888888] text-[10px] sm:text-xs">Item</p>
                              <p className="font-medium">{sol.item}</p>
                            </div>
                            <div>
                              <p className="text-[#888888] text-[10px] sm:text-xs">Falla</p>
                              <p className="font-medium">{sol.falla}</p>
                            </div>
                            <div className="col-span-2 sm:col-span-4">
                              <p className="text-[#888888] text-[10px] sm:text-xs">Descripción</p>
                              <p className="font-medium whitespace-pre-wrap">{sol.descripcion}</p>
                            </div>
                            
                            {/* Archivos - View mode */}
                            {fotos.length > 0 && (
                              <div className="col-span-2 sm:col-span-4 mt-2">
                                <p className="text-[#888888] text-[10px] sm:text-xs mb-2">Archivos</p>
                                <div className="grid grid-cols-3 gap-2">
                                  {fotos.map((foto, fotoIndex) => (
                                    <div 
                                      key={fotoIndex}
                                      className="relative aspect-square rounded-lg overflow-hidden border border-[#E8E8E7] cursor-pointer group"
                                      onClick={() => setPreviewImage(foto)}
                                      data-testid={`file-view-thumbnail-${fotoIndex}`}
                                    >
                                      <FileThumbnail url={foto} />
                                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <ZoomIn size={20} className="text-white" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      
                      {/* Edit mode */}
                      {isEditing && editingItem && (
                        <div className="space-y-3 mt-2">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                            <div>
                              <Label className="text-[10px] sm:text-xs">Categoría</Label>
                              <Select
                                value={editingItem.categoria}
                                onValueChange={(value) => {
                                  setEditingItem({
                                    ...editingItem, 
                                    categoria: value,
                                    item: '',
                                    falla: ''
                                  });
                                }}
                              >
                                <SelectTrigger className="border-[#c5b8a2] text-xs sm:text-sm h-8">
                                  <SelectValue placeholder="Seleccionar" />
                                </SelectTrigger>
                                <SelectContent>
                                  {catalogos.categorias.filter(c => c.activo !== false).map((cat) => (
                                    <SelectItem key={cat.id} value={cat.nombre}>{cat.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px] sm:text-xs">Lugar</Label>
                              <Select
                                value={editingItem.lugar}
                                onValueChange={(value) => setEditingItem({...editingItem, lugar: value})}
                              >
                                <SelectTrigger className="border-[#c5b8a2] text-xs sm:text-sm h-8">
                                  <SelectValue placeholder="Seleccionar" />
                                </SelectTrigger>
                                <SelectContent>
                                  {catalogos.lugares.filter(l => l.activo !== false).map((lugar) => (
                                    <SelectItem key={lugar.id} value={lugar.nombre}>{lugar.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px] sm:text-xs">Item</Label>
                              <Select
                                value={editingItem.item}
                                onValueChange={(value) => setEditingItem({...editingItem, item: value})}
                                disabled={!editingItem.categoria}
                              >
                                <SelectTrigger className="border-[#c5b8a2] text-xs sm:text-sm h-8">
                                  <SelectValue placeholder={editingItem.categoria ? "Seleccionar" : "Primero seleccione categoría"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {filteredItems.map((item) => (
                                    <SelectItem key={item.id} value={item.nombre}>{item.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[10px] sm:text-xs">Falla</Label>
                              <Select
                                value={editingItem.falla}
                                onValueChange={(value) => setEditingItem({...editingItem, falla: value})}
                                disabled={!editingItem.categoria}
                              >
                                <SelectTrigger className="border-[#c5b8a2] text-xs sm:text-sm h-8">
                                  <SelectValue placeholder={editingItem.categoria ? "Seleccionar" : "Primero seleccione categoría"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {filteredFallas.map((falla) => (
                                    <SelectItem key={falla.id} value={falla.nombre}>{falla.nombre}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-2 sm:col-span-4">
                              <div className="flex items-center justify-between mb-1">
                                <Label className="text-[10px] sm:text-xs">Descripción</Label>
                                <VoiceInput
                                  onTranscript={(text, isFinal) => {
                                    if (isFinal) {
                                      const currentDesc = editingItem.descripcion || '';
                                      const newDesc = currentDesc ? `${currentDesc} ${text}` : text;
                                      setEditingItem({...editingItem, descripcion: newDesc});
                                    }
                                  }}
                                />
                              </div>
                              <Textarea
                                value={editingItem.descripcion}
                                onChange={(e) => setEditingItem({...editingItem, descripcion: e.target.value})}
                                className="border-[#c5b8a2] text-xs sm:text-sm"
                                rows={2}
                                placeholder="Describa el problema o use el micrófono para dictar..."
                              />
                            </div>
                          </div>
                          
                          {/* Fotos - Edit mode */}
                          <div>
                            <Label className="text-[10px] sm:text-xs mb-2 block">Archivos (máx. 3)</Label>
                            <PhotoUpload
                              photos={editingItem.fotos || []}
                              onChange={handleFotosChange}
                              maxPhotos={3}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-[#888888] text-center py-4 text-xs sm:text-sm">No hay solicitudes registradas</p>
            )}
          </div>

          {/* Update Form */}
          {user?.rol !== 'cliente' && (
            <div className="space-y-3 sm:space-y-4 border-t border-[#E8E8E7] pt-3 sm:pt-4">
              <h4 className="font-semibold text-[#2D2D2D] text-sm sm:text-base" style={{ fontFamily: 'Playfair Display, serif' }}>
                Actualizar Requerimiento
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm">Supervisor Responsable</Label>
                  <Select value={supervisorId} onValueChange={setSupervisorId} disabled={isTerminado}>
                    <SelectTrigger data-testid="select-supervisor-responsable" className={`border-[#c5b8a2] text-sm ${isTerminado ? 'bg-gray-100 opacity-60' : ''}`}>
                      <SelectValue placeholder="Seleccionar supervisor" />
                    </SelectTrigger>
                    <SelectContent>
                      {supervisores.map((sup) => (
                        <SelectItem key={sup.id} value={sup.id}>
                          {sup.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {supervisorId && supervisores.length > 0 && !supervisores.some(s => s.id === supervisorId) && (
                    <p className="text-[11px] text-amber-700 mt-1" data-testid="supervisor-no-vigente">
                      El supervisor actual está inactivo o ya no tiene rol de supervisión. Seleccione uno vigente.
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-xs sm:text-sm">Estado</Label>
                  <Select value={estado} onValueChange={setEstado} disabled={isTerminado && !['administrador', 'jefe'].includes(user?.rol)}>
                    <SelectTrigger className={`border-[#c5b8a2] text-sm ${isTerminado && !['administrador', 'jefe'].includes(user?.rol) ? 'bg-gray-100 opacity-60' : ''}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solicitado">Solicitado</SelectItem>
                      <SelectItem value="inspeccion_pendiente">Inspección Pendiente</SelectItem>
                      <SelectItem value="inspeccion_programada">Inspección Programada</SelectItem>
                      <SelectItem value="atencion_pendiente">Atención Pendiente</SelectItem>
                      <SelectItem value="atencion_proceso">Atención en Proceso</SelectItem>
                      <SelectItem value="stand_by_empresa">Stand By Empresa</SelectItem>
                      <SelectItem value="terminado">Terminado</SelectItem>
                      <SelectItem value="cerrado">Cerrado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Inspección Programada - Fecha y Hora */}
                <div className="sm:col-span-2 bg-[#F5F5F5] p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs sm:text-sm font-semibold text-[#9d8272]">
                      Programar Inspección
                    </Label>
                    <div className="flex items-center gap-2">
                      {solicitud?.programacion_tipo && (
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          solicitud.programacion_tipo === 'manual' ? 'bg-indigo-50 text-indigo-700' : 'bg-teal-50 text-teal-700'
                        }`}>
                          {solicitud.programacion_tipo === 'manual' ? 'Manual' : 'Auto'}
                          {solicitud.programado_por_nombre && ` - ${solicitud.programado_por_nombre}`}
                        </span>
                      )}
                      {(solicitud?.reprogramaciones_count > 0) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700">
                          Reprog: {solicitud.reprogramaciones_count}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] sm:text-xs text-[#888888]">Fecha</Label>
                      <Input
                        type="date"
                        value={fechaInspeccionProgramada}
                        onChange={(e) => setFechaInspeccionProgramada(e.target.value)}
                        className={`border-[#c5b8a2] text-sm h-10 touch-manipulation ${isTerminado || isInspeccionCompletada ? 'bg-gray-100 opacity-60' : ''}`}
                        disabled={isTerminado || isInspeccionCompletada}
                        min={isTerminado || isInspeccionCompletada ? undefined : new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] sm:text-xs text-[#888888]">Hora</Label>
                      <Input
                        type="time"
                        value={horaInspeccionProgramada}
                        onChange={(e) => setHoraInspeccionProgramada(e.target.value)}
                        className={`border-[#c5b8a2] text-sm h-10 touch-manipulation ${isTerminado || isInspeccionCompletada ? 'bg-gray-100 opacity-60' : ''}`}
                        disabled={isTerminado || isInspeccionCompletada}
                      />
                    </div>
                  </div>
                  {!isTerminado && !isInspeccionCompletada && (
                    <p className="text-[10px] text-[#888888] mt-1">
                      Al programar fecha y hora, el estado cambiará a "Inspección Programada"
                    </p>
                  )}
                  {isInspeccionCompletada && !isTerminado && (
                    <p className="text-[10px] text-amber-600 mt-1">
                      La inspección ya fue completada, no se puede modificar la programación
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-xs sm:text-sm">Folio</Label>
                  <Input
                    value={idExterno}
                    onChange={(e) => setIdExterno(e.target.value)}
                    className={`border-[#c5b8a2] text-sm ${isTerminado ? 'bg-gray-100 opacity-60' : ''}`}
                    placeholder="Código externo o folio"
                    maxLength={100}
                    disabled={isTerminado}
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-xs sm:text-sm">Notas</Label>
                  <Textarea
                    value={notas}
                    onChange={(e) => setNotas(e.target.value)}
                    className="border-[#c5b8a2] text-sm"
                    rows={3}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer fijo con botones */}
        {user?.rol !== 'cliente' && !solicitud?.loading && !initialLoading && (
          <div className="shrink-0 bg-white px-4 sm:px-6 py-3 border-t border-[#E8E8E7]">
            <div className="flex flex-col-reverse sm:flex-row justify-between gap-2 sm:gap-3">
              {/* Botones izquierda: NPS + Recordatorio Autoagendamiento */}
              <div className="flex flex-wrap gap-2">
                {canSendNps && (
                  <Button
                    variant="outline"
                    onClick={handleEnviarNps}
                    disabled={sendingNps}
                    className="text-sm border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
                    data-testid="send-nps-btn"
                  >
                    {sendingNps ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <MessageCircle size={14} className="mr-2" />
                        Enviar NPS
                        {solicitud?.nps_enviado && (
                          <Badge className="ml-2 bg-green-100 text-green-700 text-[10px]">
                            Reenviado
                          </Badge>
                        )}
                      </>
                    )}
                  </Button>
                )}
                {canSendRecordatorio && (
                  <Button
                    variant="outline"
                    onClick={handleRecordarAutoagendamiento}
                    disabled={sendingRecordatorio}
                    className="text-sm border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                    data-testid="send-recordatorio-btn"
                  >
                    {sendingRecordatorio ? (
                      <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Bell size={14} className="mr-2" />
                        Recordar Autoagendamiento
                      </>
                    )}
                    <Badge className="ml-2 bg-blue-100 text-blue-700 text-[10px]">
                      Auto recordatorios: {solicitud?.recordatorios_autoagendamiento_count || 0}
                    </Badge>
                  </Button>
                )}
              </div>
              
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                <Button variant="outline" onClick={onClose} className="text-sm">Cancelar</Button>
                <Button
                  onClick={handleUpdate}
                  disabled={loading}
                  className="bg-[#9d8272] hover:bg-[#8a7162] text-white text-sm"
                >
                  {loading ? 'Actualizando...' : 'Actualizar'}
                </Button>
              </div>
            </div>
          </div>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-3xl p-2 sm:p-4">
          {previewImage && <PreviewContent url={previewImage} />}
        </DialogContent>
      </Dialog>
      
      {/* Inspection Read-Only Dialog - Outside main dialog to prevent close propagation */}
      {showInspeccionReadOnly && (
        <InspeccionDialog
          requerimiento={solicitud}
          open={showInspeccionReadOnly}
          onClose={() => setShowInspeccionReadOnly(false)}
          onUpdate={onUpdate}
          readOnly={true}
        />
      )}
      
      {/* Atencion Read-Only Dialog - Outside main dialog to prevent close propagation */}
      {showAtencionReadOnly && (
        <AtencionDialog
          requerimiento={solicitud}
          open={showAtencionReadOnly}
          onClose={() => setShowAtencionReadOnly(false)}
          onUpdate={onUpdate}
          readOnly={true}
        />
      )}
    </>
  );
};

export default SolicitudDetailDialog;
